import React from "react";

const ACTIVE_OFFSET = 80;

interface NavBarContextValue {
  activeAnchorId: string | null;
  getAnchorElement: (anchorId: string) => HTMLElement | null;
  registerAnchor: (anchorId: string, element: HTMLElement) => void;
  unregisterAnchor: (anchorId: string) => void;
}

const NavBarContext = React.createContext<NavBarContextValue>({
  activeAnchorId: null,
  getAnchorElement: () => null,
  registerAnchor: () => {},
  unregisterAnchor: () => {}
});

function getAnchorTop(element: HTMLElement): number {
  return element.getBoundingClientRect().top + globalThis.scrollY;
}

function requestFrame(callback: FrameRequestCallback) {
  if (typeof globalThis.requestAnimationFrame === "function") {
    return globalThis.requestAnimationFrame(callback);
  }

  return globalThis.setTimeout(() => callback(Date.now()), 0);
}

function cancelFrame(frameId: ReturnType<typeof requestFrame>) {
  if (typeof globalThis.cancelAnimationFrame === "function") {
    globalThis.cancelAnimationFrame(frameId as number);
    return;
  }

  globalThis.clearTimeout(frameId as ReturnType<typeof globalThis.setTimeout>);
}

export const NavBarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const anchors = React.useRef(new Map<string, HTMLElement>());
  const frame = React.useRef<ReturnType<typeof requestFrame> | null>(null);
  const [activeAnchorId, activeAnchorIdSet] = React.useState<string | null>(null);

  const updateActiveAnchor = React.useCallback(() => {
    const anchorsSorted = [...anchors.current.entries()]
      .filter(([, element]) => element.isConnected)
      .sort((left, right) => getAnchorTop(left[1]) - getAnchorTop(right[1]));
    const scrollPosition = globalThis.scrollY + ACTIVE_OFFSET;
    let activeAnchorIdNext: string | null = null;

    for (const [anchorId, element] of anchorsSorted) {
      if (scrollPosition >= getAnchorTop(element)) {
        activeAnchorIdNext = anchorId;
      } else {
        break;
      }
    }

    activeAnchorIdSet((current) => (current === activeAnchorIdNext ? current : activeAnchorIdNext));
  }, []);

  const scheduleActiveAnchorUpdate = React.useCallback(() => {
    if (frame.current !== null) {
      return;
    }

    frame.current = requestFrame(() => {
      frame.current = null;
      updateActiveAnchor();
    });
  }, [updateActiveAnchor]);

  const registerAnchor = React.useCallback(
    (anchorId: string, element: HTMLElement) => {
      anchors.current.set(anchorId, element);
      scheduleActiveAnchorUpdate();
    },
    [scheduleActiveAnchorUpdate]
  );

  const unregisterAnchor = React.useCallback(
    (anchorId: string) => {
      anchors.current.delete(anchorId);
      scheduleActiveAnchorUpdate();
    },
    [scheduleActiveAnchorUpdate]
  );

  const getAnchorElement = React.useCallback((anchorId: string) => {
    return anchors.current.get(anchorId) || null;
  }, []);

  React.useEffect(() => {
    scheduleActiveAnchorUpdate();

    globalThis.addEventListener("scroll", scheduleActiveAnchorUpdate, { passive: true });
    globalThis.addEventListener("resize", scheduleActiveAnchorUpdate);
    globalThis.addEventListener("hashchange", scheduleActiveAnchorUpdate);

    return () => {
      globalThis.removeEventListener("scroll", scheduleActiveAnchorUpdate);
      globalThis.removeEventListener("resize", scheduleActiveAnchorUpdate);
      globalThis.removeEventListener("hashchange", scheduleActiveAnchorUpdate);

      if (frame.current !== null) {
        cancelFrame(frame.current);
      }
    };
  }, [scheduleActiveAnchorUpdate]);

  return (
    <NavBarContext.Provider
      value={{
        activeAnchorId,
        getAnchorElement,
        registerAnchor,
        unregisterAnchor
      }}
    >
      {children}
    </NavBarContext.Provider>
  );
};

export const useNavBarContext = () => React.useContext(NavBarContext);
